import * as React from 'react';

// mui
import { Box, Typography } from '@material-ui/core';

// app
import { Documents, Empty, FormGrid } from 'components';
import { RiskDocumentUpload } from './RiskDocumentUpload';
import { ReactComponent as Loading } from 'assets/svg/loading.svg';
import { useDeleteRiskDocument, useDownloadRiskDocument, useGetRiskDocuments } from 'lib/quoteBind';
import * as utils from 'utils';
import { useQuoteBindStyles } from 'modules/QuoteBind';

const documentsColumns = [
  { id: 'folder', label: utils.string.t('app.folder') || 'Folder' },
  { id: 'filename', label: utils.string.t('app.filename') || 'Filename' },
  { id: 'uploadedBy', label: utils.string.t('app.uploadedBy') || 'Uploaded By' },
  { id: 'uploadedDate', label: utils.string.t('app.uploadedDate') || 'Uploaded Date' },
  { id: 'actions', empty: true },
];

export const RiskDocuments = ({ riskId }) => {
  const classes = useQuoteBindStyles();
  const { data, isLoading } = useGetRiskDocuments(riskId);
  const { mutate: deleteRiskDocument } = useDeleteRiskDocument();
  const { mutate: downloadRiskDocument } = useDownloadRiskDocument();

  const handleDeleteRiskDocument = (documentId) => {
    deleteRiskDocument({ riskId, documentId });
  };

  const handleDownloadRiskDocument = (data) => {
    const { id: documentId, fileName, contentType } = data;
    downloadRiskDocument({ riskId, documentId, fileName, contentType });
  };

  const handlers = {
    handleDelete: handleDeleteRiskDocument,
    handleDownload: handleDownloadRiskDocument,
  };

  const getFoldersAndDocuments = (initialData = []) => {
    const foldersMap = new Map();
    const formattedDocuments = [];

    const formatDocument = (doc) => ({
      id: doc.fileId,
      fileName: doc.name,
      type: doc.name.split('.').pop(),
      size: doc.length,
      contentType: doc.contentType,
      uploaderFullName: doc?.metaData?.userName || '',
      createdAt: doc?.metaData?.createdAt,
      folder: doc?.metaData?.group || 'No Folder',
    });

    const addNewFolder = (name) => {
      // if folder already exists skip
      if (!foldersMap.has(name)) {
        foldersMap.set(name, { id: name, label: name, value: name });
      }
    };

    // I added recursion in case we have a more complex tree structure in the future
    const separateFoldersFromDocuments = (data) => {
      data.forEach((item) => {
        // this means it is a file
        if (item.children === null) {
          formattedDocuments.push(formatDocument(item));

          // for old data types when folder name was under metaData or not exist at all
          addNewFolder(item.metaData.group || 'No Folder');
        } else {
          addNewFolder(item.name || 'No Folder');

          if (item.children.length) {
            separateFoldersFromDocuments(item.children);
          }
        }
      });
    };

    separateFoldersFromDocuments(initialData);

    const folders = [...foldersMap.values()];

    return { folders, formattedDocuments };
  };

  const { folders, formattedDocuments } = getFoldersAndDocuments(data);

  return (
    <Box className={classes.root}>
      <Box mb={2} display="flex" alignItems="center" justifyContent="space-between" className={classes.header}>
        <Typography variant="h3" style={{ marginBottom: 0, marginRight: 10, fontWeight: 600 }}>
          {utils.string.t('risks.documents')}
        </Typography>
      </Box>
      <Box className={classes.content}>
        <FormGrid container spacing={5}>
          <FormGrid
            item
            xs={12}
            sm={6}
            md={8}
            data-testid="risk-documents-list"
            style={{
              borderRight: '1px solid #e0e0e0',
            }}
          >
            {isLoading ? (
              <Empty title={utils.string.t('app.loading')} icon={<Loading />} width={300} />
            ) : (
              <Documents
                documents={formattedDocuments}
                folders={folders}
                columns={documentsColumns}
                handlers={handlers}
                displayUserAvatar
              />
            )}
          </FormGrid>
          <FormGrid item xs={12} sm={6} md={4} data-testid="risk-documents-upload">
            <RiskDocumentUpload riskId={riskId} groups={folders.filter((folder) => folder.label)} />
          </FormGrid>
        </FormGrid>
      </Box>
    </Box>
  );
};
